import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Alert, Button, Stack, Typography } from '@mui/material'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'

import { useOrders } from '../../hooks/useOrders'
import { theme } from '../../theme'

const MAX_FILE_SIZE = 10 * 1024 * 1024
const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png']

const getSecondaryActionSx = () => ({
  backgroundColor: '#F5F5F5 !important',
  color: `${theme.palette.fontColor.dark} !important`,
  border: `1px solid ${theme.palette.borderColor.main} !important`,
  borderRadius: '9px',
  whiteSpace: 'nowrap',
  boxShadow: 'none !important',
  minHeight: '30px',
  px: 1.05,
  py: 0.35,
  fontSize: '10px',
  fontWeight: 700,
  lineHeight: 1.7,
  '&:hover': {
    backgroundColor: '#EBEBEB !important',
    borderColor: `${theme.palette.fontColor.main} !important`
  }
})

const canUploadProof = (order) => (
  order?.payment_method === 'bank_transfer' &&
  order?.payment_status !== 'paid' &&
  order?.bank_transfer_status !== 'confirmed' &&
  order?.bank_transfer_status !== 'proof_uploaded'
)

const BankTransferProofUploader = ({ order, onUploaded }) => {
  const { uploadBankTransferProof } = useOrders()
  const inputRef = useRef(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')

  if (order?.payment_method !== 'bank_transfer') {
    return null
  }

  if (order?.bank_transfer_status === 'proof_uploaded') {
    return (
      <Stack spacing={0.75}>
        <Alert severity='success' sx={{ '& .MuiAlert-message': { fontSize: '10px', lineHeight: 1.7 } }}>
          Dovada platii a fost trimisa. Echipa noastra o verifica acum.
        </Alert>
        <Typography variant='caption' sx={{ color: theme.palette.fontColor.medium, fontSize: '9.5px', lineHeight: 1.6 }}>
          Butonul de incarcare ramane dezactivat pana cand echipa noastra valideaza sau respinge documentul.
        </Typography>
      </Stack>
    )
  }

  if (order?.payment_status === 'paid' || order?.bank_transfer_status === 'confirmed') {
    return (
      <Alert severity='success' sx={{ '& .MuiAlert-message': { fontSize: '10px', lineHeight: 1.7 } }}>
        Plata a fost confirmata.
      </Alert>
    )
  }

  const isRejected = order?.bank_transfer_status === 'rejected'

  if (!canUploadProof(order) && !isRejected) {
    return null
  }

  const openPicker = () => {
    setError('')
    inputRef.current?.click()
  }

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) {
      return
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Acceptam doar PDF, JPG, JPEG sau PNG.')
      return
    }
    if (file.size > MAX_FILE_SIZE) {
      setError('Fisierul nu poate depasi 10 MB.')
      return
    }

    try {
      setUploading(true)
      setError('')
      const payload = await uploadBankTransferProof(order._id, file)
      onUploaded?.(payload.data || null)
    } catch (uploadError) {
      setError(uploadError.message)
    } finally {
      setUploading(false)
    }
  }

  return (
    <Stack spacing={0.75}>
      <input
        ref={inputRef}
        type='file'
        accept='.pdf,.jpg,.jpeg,.png'
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      <Button
        size='small'
        variant='contained'
        onClick={openPicker}
        startIcon={<UploadFileOutlinedIcon />}
        disabled={uploading}
        sx={getSecondaryActionSx()}
      >
        {uploading ? 'Se incarca dovada...' : (isRejected ? 'Incarca dovada noua' : 'Incarca dovada platii')}
      </Button>
      {isRejected && (
        <Alert severity='warning' sx={{ '& .MuiAlert-message': { fontSize: '10px', lineHeight: 1.7 } }}>
          Dovada platii a fost respinsa. Incarca un document nou pentru reverificare.
        </Alert>
      )}
      <Typography variant='caption' sx={{ color: theme.palette.fontColor.medium, fontSize: '9.5px', lineHeight: 1.6 }}>
        PDF, JPG, JPEG sau PNG, maximum 10 MB.
      </Typography>
      {error && <Alert severity='warning' sx={{ '& .MuiAlert-message': { fontSize: '10px', lineHeight: 1.7 } }}>{error}</Alert>}
    </Stack>
  )
}

BankTransferProofUploader.propTypes = {
  order: PropTypes.object,
  onUploaded: PropTypes.func
}

export default BankTransferProofUploader
